<script>
    import Vue from "vue";
    import {isMobile} from "mobile-device-detect";
    import {mapState} from "vuex";

    import NoteEdit from "@/pages/NoteEdit.vue";
    import PasswordEdit from "@/pages/PasswordEdit.vue";

    export default {
        name: "Passwords",
        components: {
            NoteEdit,
            PasswordEdit,
        },
        computed: {
            ...mapState({
                apiRoot: state => state.apiRoot,
                passwords: state => state.passwords,
                notes: state => state.notes,
                user: state => state.user,
                backup: state => state.backup,
                recentlyUsed: state => {
                    // we store the 50 most recently used passwords and show the 5 most often used ones
                    // note the difference between recently and often. this is a somewhat arbitrary balance
                    let allRecent = state.recentlyUsed;
                    let frequency = {};
                    allRecent.forEach(id => {
                        frequency[id] = (frequency[id] || 0) + 1;
                    });
                    let top = Object.entries(frequency)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 7);

                    let byId = {};
                    state.passwords.forEach(pass => (byId[pass.id] = pass));
                    top = top.map(r => byId[r[0]]);
                    return top;
                },
            }),

            filtered(val) {
                let items;
                let searchFields = [];
                if (this.showing == "passwords") {
                    items = [...this.passwords];
                    searchFields = ["site", "notes", "login"];
                } else {
                    items = this.notes.filter(rec => rec.type == this.showing);
                    searchFields = ["title", "description"];
                }

                if (this.filter) {
                    items = items.filter(rec => {
                        let found = false;
                        searchFields.forEach(field => {
                            found = found || (rec[field] || "").toLowerCase().indexOf(this.filter.toLowerCase()) != -1;
                        });
                        return found;
                    });
                }
                return items.sort((a, b) => (a[searchFields[0]] || "").localeCompare(b[searchFields[0]] || ""));
            },
        },
        data() {
            return {
                userProfile: null,
                filter: "",
                currentRow: null,
                currentIdx: -1,
                editing: null,
                scrolled: false,
                lastActivityTs: new Date(),
                showing: "passwords",
            };
        },

        methods: {
            changeShowing(showing) {
                this.showing = showing;
            },

            importFromFile(ev) {
                let file = ev.target.files[0];
                this.$store.dispatch("importPasswords", file);
                this.$refs.fileLoader.value = null; // set back to zero, so a double import would still do something
            },

            setCurrentIdx(idx) {
                if (isMobile) {
                    // currentIdx makes no sense on mob as you can't use keys
                    return;
                }
                this.currentIdx = idx;
            },

            onAction(action) {
                let actions = {
                    newPassword: () => this.newRec(),
                    showPasswords: () => this.changeShowing("passwords"),
                    showPlaces: () => this.changeShowing("places"),
                    showPeople: () => this.changeShowing("people"),
                    showNotes: () => this.changeShowing("notes"),
                    importLockbox: () => this.importPasswords(),
                    exportLockbox: () => this.exportJSON(),
                    logOut: () => this.logOut(),
                    changeMasterPass: () => this.$store.commit("go", "change-passphrase"),
                };
                actions[action]();
            },

            logOut() {
                // kill all cookies and wipe all state
                this.$store.dispatch("logOut");
            },

            openActions(ev, idx) {
                setCurrentIdx(idx);
                this.currentRow = this.filtered[idx];
                this.$refs["action-menu"].showAt(ev.pageX, ev.pageY);
            },

            editPass(creds) {
                //this.setCurrentIdx(idx);
                this.currentRow = creds;
                this.editing = "password";
            },

            editNote(note) {
                //this.setCurrentIdx(idx);
                this.currentRow = note;
                this.editing = "note";
            },

            onFilterChanged(ev) {
                // v-model on mobile updates only on blur for some reason, so we set it explicitly
                this.filter = this.$refs.filter.value;
                this.setCurrentIdx(0);
            },

            copyField(creds, field) {
                navigator.clipboard.writeText(creds.pass);

                if (field == "pass") {
                    this.$store.dispatch("trackCopy", creds);
                    this.$refs["copy-prompt"].notify(creds.site);
                }
            },

            checkKey(ev) {
                if (this.editing) {
                    return;
                }

                let actions = {
                    ArrowUp: () => {
                        this.setCurrentIdx(Math.max(0, this.currentIdx - 1));
                    },
                    ArrowDown: () => {
                        this.setCurrentIdx(Math.min(this.filtered.length - 1, this.currentIdx + 1));
                    },
                    Enter: () => {
                        if (this.currentIdx >= 0) {
                            this.editRow(this.currentIdx);
                        } else {
                            return false;
                        }
                    },
                    Tab: () => {
                        // on tab we drop any current row pretense
                        this.setCurrentIdx(-1);
                        return false;
                    },
                    Escape: () => {
                        this.clearSearch();
                    },
                };

                let action = actions[ev.key];
                if (action) {
                    let handled = action();

                    if (handled != false) {
                        ev.preventDefault();
                        ev.stopPropagation();

                        // scroll to current row
                        if (this.currentIdx >= 0) {
                            let row = this.$refs[`row${this.currentIdx}`][0];
                            let rect = row.getBoundingClientRect();
                            window.scrollTo(0, window.scrollY + rect.top - 100);
                        }
                    }
                } else {
                    //console.log("catch me", ev);
                }
            },

            updatePassword(creds) {
                this.editing = null;
                if (!creds) {
                    return;
                }
                this.$store.dispatch("updatePass", creds);
                this.refocus();
            },
            deletePassword(creds) {
                this.editing = null;
                this.$store.dispatch("deletePass", creds);
                this.refocus();
            },

            updateNote(note) {
                this.editing = null;
                if (!note) {
                    return;
                }
                this.$store.dispatch("updateNote", note);
                this.refocus();
            },
            deleteNote(note) {
                this.editing = null;
                this.$store.dispatch("deleteNote", note);
                this.refocus();
            },

            refocus() {
                // refocus to the searchbox if we're unlocked and are not on mobile
                // reason to avoid mobile is to avoid the onscreen keyboard eating up valuable space
                if (!isMobile) {
                    Vue.nextTick(() => {
                        this.$refs.filter.focus();
                    });
                }
            },

            newRec() {
                this.currentRow = {};
                this.editing = this.showing == "passwords" ? "password" : "note";
            },

            importPasswords() {
                // show file dialog, read the file, parse it, then deal with it
                this.$refs.fileLoader.click();
            },

            exportJSON() {
                this.$store.dispatch("exportJSON");
            },

            handleScroll() {
                this.scrolled = window.scrollY > 0;
            },

            clearSearch() {
                this.filter = "";
                this.refocus();
            },

            setlastActivityTs() {
                this.lastActivityTs = new Date();
            },

            checkForInactivity() {
                // lock after 5 minutes of inactivity
                // XXX - move this to store
                let delta = new Date() - this.lastActivityTs;
                let timeout = 1000 * 60 * 5;

                if (delta > timeout) {
                    console.log("Locked the pack due to inactivity");
                    this.$store.dispatch("requestLock");
                }
            },
        },

        mounted() {
            if (this.backup) {
                // backup is in store only if we timed out during an edit
                this.showing = this.backup.editing == "password" ? "passwords" : this.backup.creds.type;
                this.currentRow = this.backup.creds;
                this.editing = this.backup.editing;
                // after showing backup, we reset it so it doesn't stalk us
                this.$store.commit("setBackup", null);
                this.$store.commit("encryptPack");
            }
            this.refocus();
            this.inactivityCheck = window.setInterval(this.checkForInactivity, 5000);

            document.body.style["overflow-y"] = "scroll";
            window.addEventListener("scroll", this.handleScroll);
            window.addEventListener("mousemove", this.setlastActivityTs);
            window.addEventListener("keypress", this.setlastActivityTs);
            window.addEventListener("keydown", this.checkKey);
        },
        beforeDestroy() {
            document.body.style["overflow-y"] = "auto";
            window.clearInterval(this.inactivityCheck);
            window.removeEventListener("scroll", this.handleScroll);
            window.removeEventListener("mousemove", this.setlastActivityTs);
            window.removeEventListener("keypress", this.setlastActivityTs);
        },
    };
</script>


<template>
    <div class="main">
        <div class="layout" :class="{'modal-running': editing != null}">
            <div class="toolbar">
                <div class="search-container">
                    <div class="searchbox">
                        <button class="icon-button search-button">
                            <i class="material-icons">search</i>
                        </button>

                        <input
                            type="text"
                            autocomplete="off"
                            autocorrect="off"
                            autocapitalize="off"
                            spellcheck="false"
                            class="filter"
                            ref="filter"
                            v-model="filter"
                            @input="onFilterChanged($event)"
                        />

                        <button class="icon-button" @click="clearSearch">
                            <i class="material-icons">clear</i>
                        </button>
                    </div>

                    <div class="scroll-shadow" :class="{scrolled: scrolled}" />
                </div>
            </div>

            <div class="contents">
                <div class="items" v-if="showing == 'passwords' && passwords.length">
                    <div v-show="!filter.length && recentlyUsed.length">
                        <div class="header">
                            Recent &amp; Frequent
                        </div>

                        <password-row
                            v-for="creds in recentlyUsed"
                            :key="creds.id"
                            :creds="creds"
                            @onCopy="copyField"
                            @onEdit="editPass"
                        />
                    </div>

                    <div class="header" v-show="!filter.length">
                        A-Z
                    </div>
                    <password-row
                        v-for="(creds, idx) in filtered"
                        :key="creds.id"
                        :creds="creds"
                        @onCopy="copyField"
                        @onEdit="editPass"
                        :class="{current: idx == currentIdx}"
                        :ref="'row' + idx"
                    />
                </div>

                <div class="items" v-if="showing != 'passwords' && notes.length">
                    <note-row
                        v-for="(note, idx) in filtered"
                        :key="note.id"
                        :note="note"
                        @onCopy="copyField"
                        @onEdit="editNote"
                        :class="{current: idx == currentIdx}"
                        :ref="'row' + idx"
                    />
                </div>

                <div v-if="showing == 'passwords' && !passwords.length" class="welcome-message">
                    <p>
                        Hey and welcome to your lockbox!
                    </p>
                    <p>
                        Apart from storing passwords, you can also store notes on people, places, and general notes. So
                        you'd remember who was that person you talked to last friday in that meetup, and to remember to
                        stay away from the takeaway that brought you soggy tacos. General notes are, of course, for for
                        anything that doesn't fit the bill.
                    </p>
                    <p>
                        The search box on top should come handy once you have more than just a handful items in your
                        lockbox. Anyway, what you are waiting for - go ahead - hit the New Password button and add your
                        first lockbox password!
                    </p>
                </div>

                <div v-if="showing != 'passwords' && !notes.length" class="welcome-message">
                    <div v-if="showing == 'places'">
                        <p>
                            Ahhh, the places we've been to and places we've yet to see!
                        </p>
                        <p>
                            This place, though, is the place where you can jot it all down. This is your private yelp,
                            your square space, and your google reviews, all in one. The best part? Nobody needs to know
                            your deepest and most sincere opinions about that pizza place, and that hair saloon, it's
                            all for your eyes only. Burn before reading!
                        </p>
                    </div>
                    <div v-if="showing == 'people'">
                        <p>
                            Names and faces, faces and names, who can remember them all!
                        </p>
                        <p>
                            Meetups and conferences and other bigger gatherings can be overwhelming - so many new faces
                            and names to remember. You add them to your facebooks and instagrams, but who, three months
                            down the line, can still remember why they are there?
                        </p>
                        <p>
                            So, write it down while the memories are still fresh - what they like and how you know them,
                            do you have common friends, and do they have accounts in other social networks.
                        </p>
                        <p>And because it's <i>your</i> lockbox, it all is <i>your</i> eyes only.!</p>
                    </div>

                    <div v-if="showing == 'notes'">
                        <p>
                            This is the wildcard, the anything goes place
                        </p>
                        <p>
                            Stuff that doesn't fit anywhere else. If you have an idea for a category in lockbox, get in
                            touch. But other than that - nothing wrong with random scribblings. Go on - push that "New
                            Note" button!
                        </p>
                    </div>
                </div>

                <Footer />
            </div>
        </div>

        <ActionsPanel @onAction="onAction" :currentPage="showing" />

        <CopyPrompt ref="copy-prompt" />

        <transition name="modal">
            <PasswordEdit
                :creds="currentRow"
                v-if="editing == 'password'"
                @save="updatePassword"
                @delete="deletePassword"
            />
            <NoteEdit
                :note="currentRow"
                :type="showing"
                v-if="editing == 'note'"
                @save="updateNote"
                @delete="deleteNote"
            />
        </transition>

        <input type="file" @change="importFromFile" ref="fileLoader" class="file-picker" />
    </div>
</template>


<style lang="scss">
    .main {
        background: #dae3df;

        .file-picker {
            display: none;
        }

        .filter {
            display: block;
            width: 100%;
            font-size: 1em;
            padding: 5px;
            margin: 1em 0;
        }

        .modal-running {
            overflow: hidden;
            filter: blur(1px);
        }

        .modal-enter-active,
        .modal-leave-active {
            transition: opacity 0.3s, margin 0.2s;
        }

        .modal-enter,
        .modal-leave-to {
            opacity: 0;
            margin-top: 25px;
        }

        .toolbar {
            position: fixed;
            z-index: 500;
            top: 0;
            left: 0;
            right: 0;
            display: flex;
            align-items: center;
            background: #fff;

            height: $toolbar-height;

            max-width: $max-width;
            margin: 0 auto;
        }

        .scroll-shadow {
            border-bottom: 1px solid #ddd;
            height: 10px;
            &.scrolled {
                box-shadow: 0 3px 0px #ddd;
            }
        }

        .logo {
            width: $logo-width;
            font-size: 1.2em;
            margin-right: 0;
            display: flex;
            align-items: center;
            justify-content: left;
            padding-left: 1em;

            .emoji {
                margin-right: 5px;
                padding: 8px;
                width: 50px;
                height: 50px;
            }
        }

        .search-container {
            flex-grow: 1;

            .searchbox {
                margin-left: $logo-width + $horiz-padding;

                background: #f2f4f4;
                border-radius: 6px;
                display: flex;
                padding: 5px;
                margin-right: $horiz-padding;

                input {
                    flex-grow: 1;
                    border: none;
                    background: none;
                    margin: 0;
                    padding: 10px;
                    outline: none;
                }

                button {
                    text-decoration: none;
                    padding: 8px;
                    display: flex;
                    align-items: center;
                    color: #888;
                    border-radius: 50%;

                    &:focus,
                    &:hover {
                        outline: none;
                        background: #e6e6e6;
                    }
                }
            }
        }

        .layout {
            min-height: 100vh;
            display: flex;
            flex-direction: column;
        }

        .layout > .contents {
            padding-top: $toolbar-height;
            padding-left: $logo-width;
            flex-grow: 1;
            display: flex;
            flex-direction: column;

            margin: 0 auto;
            background: #fff;
            width: 100%;
            max-width: $max-width;
            box-shadow: 0px 0px 20px rgba(64, 100, 84, 0.4);
        }

        @media (max-width: $max-width + 15) {
            .layout > .contents {
                box-shadow: none;
                max-width: $max-width + 50;
            }

            .toolbar {
                max-width: $max-width + 50;
            }
        }

        .items {
            flex-grow: 1;
        }

        .header {
            font-weight: 500;
            margin: 5px 0;
            border-bottom: 1px solid #ddd;
            padding: 15px;
        }

        .welcome-message {
            margin: 0 2em;
            margin-top: 3em;
            line-height: 170%;
        }
    }

    @media (max-width: $mobile-width-break) {
        .main {
            .search-container .searchbox {
                margin-left: $horiz-padding-mobile;
                margin-right: $horiz-padding-mobile;
            }

            .layout > .contents {
                margin-left: 0;
                margin-right: 0;
                padding-left: 0;
            }
        }
    }

    @media (max-width: 600px) {
        .main {
            .searchbox {
                .search-button {
                    display: none;
                }
            }
        }
    }
</style>


